/**
 * The complete org permission vocabulary (docs/03), one file per group.
 *
 * Append-only registry (docs/01 rule 6): a work package adds permissions to its own group
 * file (or adds a new file plus ONE import line and ONE entry below) and updates docs/03 and
 * roles.ts in the same commit. Nothing else in this file changes.
 */
import { billingDocsPermissions } from './billing-docs.js';
import { billingPermissions } from './billing.js';
import { cataloguePermissions } from './catalogue.js';
import { chargesPermissions } from './charges.js';
import { communityPermissions } from './community.js';
import { connectionsPermissions } from './connections.js';
import { directoryPermissions } from './directory.js';
import { disputesPermissions } from './disputes.js';
import { membersPermissions } from './members.js';
import { messagingPermissions } from './messaging.js';
import { orgPermissions } from './org.js';
import { ratesPermissions } from './rates.js';
import { settlementPermissions } from './settlement.js';
import { shipmentsPermissions } from './shipments.js';
import { toolsPermissions } from './tools.js';
import { verificationPermissions } from './verification.js';

export const PERMISSION_GROUPS = {
  org: orgPermissions,
  members: membersPermissions,
  billing: billingPermissions,
  directory: directoryPermissions,
  connections: connectionsPermissions,
  messaging: messagingPermissions,
  verification: verificationPermissions,
  catalogue: cataloguePermissions,
  rates: ratesPermissions,
  shipments: shipmentsPermissions,
  charges: chargesPermissions,
  'billing-docs': billingDocsPermissions,
  settlement: settlementPermissions,
  disputes: disputesPermissions,
  tools: toolsPermissions,
  community: communityPermissions,
} as const;

export type PermissionGroup = keyof typeof PERMISSION_GROUPS;

/** Org permission: string-literal union of every group. */
export type Permission = (typeof PERMISSION_GROUPS)[PermissionGroup][number];

export const PERMISSIONS: readonly Permission[] = Object.values(PERMISSION_GROUPS).flat();

const PERMISSION_SET: ReadonlySet<string> = new Set(PERMISSIONS);

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && PERMISSION_SET.has(value);
}

/** `*:read` permissions (used by the viewer role and the platform support read rule). */
export function isReadPermission(permission: string): boolean {
  return permission.endsWith(':read');
}

export * from './billing-docs.js';
export * from './billing.js';
export * from './catalogue.js';
export * from './charges.js';
export * from './community.js';
export * from './connections.js';
export * from './directory.js';
export * from './disputes.js';
export * from './members.js';
export * from './messaging.js';
export * from './org.js';
export * from './rates.js';
export * from './settlement.js';
export * from './shipments.js';
export * from './tools.js';
export * from './verification.js';
